import { useState } from "react";
import { Button } from "@sortera/react-components";
import { ArrowPathIcon, PlusIcon } from "@sortera/react-icons";
import { Spinner } from "../../ui/spinner/index.ts";
import { StartPage, StartPageActions, StartPageLead, StartPageNote } from "../../ui/start-page/index.ts";
import { CreateProjectDialog } from "../create-project/index.ts";
import styles from "./no-projects.module.css";

/** Where Sortera Factory runs in Azure DevOps, for whoever would rather start one there. */
export const FACTORY_PIPELINES = "https://dev.azure.com/SorteraOne/Sortera%20Lab/_build";

/**
 * The start page for someone with no project yet: make one, or look again. Without `onCreated`
 * (making projects from here is switched off) it points to the Factory in DevOps instead.
 */
export function NoProjects({
  defaultEmail,
  onCreated,
  onRefresh,
}: {
  defaultEmail: string;
  onCreated?: (repo: string) => void;
  onRefresh: () => Promise<void>;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <StartPage
      title="Inget projekt ännu"
      after={
        onCreated && (
          <CreateProjectDialog open={creating} onOpenChange={setCreating} defaultEmail={defaultEmail} onCreated={onCreated} />
        )
      }
    >
      {onCreated ? (
        <>
          <StartPageLead>
            Sortera Factory skapar projektet åt dig. Det tar ungefär en minut, och sedan bygger du vidare här.
          </StartPageLead>

          <ol className={styles.steps}>
            <li>
              <strong>Klicka på Skapa projekt.</strong> Allt görs härifrån — du behöver inte gå till Azure DevOps.
            </li>
            <li>
              <strong>Ge projektet ett namn.</strong> Kort, högst 18 tecken. Välj poc om du tvekar.
            </li>
            <li>
              <strong>Vänta en minut.</strong> Repo, pipeline och testmiljö skapas, sedan öppnas projektet här.
            </li>
          </ol>
        </>
      ) : (
        <StartPageLead>
          Projekt skapas med <a href={FACTORY_PIPELINES} target="_blank" rel="noreferrer">Sortera Factory i Azure DevOps</a>.
          Det tar ungefär en minut, och sedan syns projektet här.
        </StartPageLead>
      )}

      <StartPageActions>
        {onCreated && (
          <Button color="secondary" size="small" icon={<PlusIcon />} onClick={() => setCreating(true)}>
            Skapa projekt
          </Button>
        )}
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          icon={refreshing ? <Spinner label="Hämtar" /> : <ArrowPathIcon />}
          disabled={refreshing}
          onClick={() => void refresh()}
        >
          Visa mina projekt igen
        </Button>
      </StartPageActions>

      <StartPageNote>Du ser bara projekt som du själv har skapat.</StartPageNote>
    </StartPage>
  );
}
